import { DiscountPerformance, DiscountUser } from './DiscountDetails.model';
import { ArrayItem, SkipNull } from '@client/new/helpers/types';
import {
  BundleStatus,
  CouponProductType,
  GetCouponDetailsQuery,
  UserStatusAtRedemption,
} from 'generated/graphql';
import { Discount, DiscountProduct } from './Discounts.model';
import { mapAmount, mapStatus } from './Discounts.mappers';
import {
  toPricesVM,
  ToPricesVMParams,
} from '../view-model/newDiscount.mappers';
import {
  toProgramCheckoutUrl,
  toSubscriptionPurchasePage,
} from '@client/helpers/routes';
import { ensureRange, formatNumberWithPrecision } from '../view-model/utils';

export type ServerCouponDetails = GetCouponDetailsQuery['getCouponDetails'];
export type ServerCoupon = ServerCouponDetails['coupon'];
export type ServerUsers = ServerCouponDetails['redemptionsList'];
export type ServerUser = SkipNull<ArrayItem<SkipNull<ServerUsers>>>;

export const toDiscountUser = (
  u: ServerUser,
  productType: DiscountProduct['type'],
): DiscountUser => {
  const res: DiscountUser = {
    email: u.email || '',
    name: u.name || '-',
    purchaseDate: u.redeemedAt,
    userType: 'New',
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  res.userType = u.userStatusAtRedemption as any;
  if (u.userStatusAtRedemption === UserStatusAtRedemption.ActiveSubscriber)
    res.userType = 'Active Subscriber';
  else if (
    u.userStatusAtRedemption === UserStatusAtRedemption.Returning &&
    productType === 'otp'
  )
    res.userType = 'Returning OTP';
  return res;
};

export const toDiscountUsers = (
  users: ServerUsers,
  productType: DiscountProduct['type'],
): DiscountUser[] =>
  (users || [])
    .filter((u) => Boolean(u))
    .map((u) => toDiscountUser(u as ServerUser, productType));

export const toDiscountPerformance = (
  discount: Discount,
  v: GetCouponDetailsQuery['getCouponDetails'],
): DiscountPerformance => {
  const price = getProductPrice(v);
  const isFixed = discount.amount.type === 'fixed';
  const value = formatNumberWithPrecision(discount.amount.value / 100, 2);
  const params: ToPricesVMParams = {
    discountType: isFixed ? 'FixedAmount' : 'Percentage',
    discountFixedValue: isFixed ? value : 0,
    discountPercentValue: isFixed ? 0 : discount.amount.value,
  };
  const pvm = toPricesVM(price, params);

  const discountedPrice = Math.floor(
    parseFloat(pvm.discountedPrice?.join('.') || '0') * 100,
  );

  const totalRedemptions = v.metrics?.totalRedemptions || 0;
  const subTotalConversion = v.metrics?.upgrading || 0;
  const otpTotalConversion = v.metrics?.activeSubscriber || 0;
  const totalConversion =
    discount.product.type === 'subscription'
      ? subTotalConversion
      : otpTotalConversion;

  const totalConversionPercent = ensureRange(
    totalRedemptions ? (totalConversion * 100) / totalRedemptions : 0,
    [0, 100],
    1,
  );

  return {
    totalRedemptions,
    totalConversionPercent,
    discountedPrice,
    totalConversion,
  };
};

const getProductPrice = (res: ServerCouponDetails): number => {
  const coupon: ServerCoupon = res.coupon;
  if (!coupon) throw new Error('Coupon is not defined');
  if (coupon.productType === CouponProductType.Subscription) {
    const sub = res.subscriptionTierPrice;
    return sub?.price || 0;
  }
  if (coupon.productType === CouponProductType.OtpBundle) {
    const offer =
      res.paidOtpBundles && res.paidOtpBundles.length
        ? res.paidOtpBundles[0]
        : null;
    return offer?.oneTimeProduct?.price || 0;
  }
  const otp =
    res.paidPrograms && res.paidPrograms.length ? res.paidPrograms[0] : null;
  return otp?.exclusiveProgramMetadata?.price || 0;
};

export const mapProduct = (res: ServerCouponDetails): DiscountProduct => {
  const coupon: ServerCoupon = res.coupon;
  if (!coupon) throw new Error('Coupon is not defined');

  if (coupon.productType === CouponProductType.Subscription) {
    const sub = res.subscriptionTierPrice;
    const period = sub?.billingCycleInterval === 'Year' ? 'annual' : 'monthly';
    return { type: 'subscription', value: { period } };
  }

  if (coupon.productType === CouponProductType.OtpBundle) {
    const bundle =
      res.paidOtpBundles && res.paidOtpBundles.length
        ? res.paidOtpBundles[0]
        : null;
    const bundleId = '' + (bundle?.id || 'N/A');
    const name = bundle?.title || 'N/A';
    return { type: 'otp-bundle', value: { bundleId, name } };
  }

  const otp =
    res.paidPrograms && res.paidPrograms.length ? res.paidPrograms[0] : null;
  const otpId = '' + (otp?.id || 'N/A');
  const name = otp?.name || 'N/A';
  return { type: 'otp', value: { otpId, name } };
};

export const toDiscount = (
  res: ServerCouponDetails,
  customURL: string,
): Discount => {
  const coupon: ServerCoupon = res.coupon;
  if (!coupon) throw new Error('Coupon is not defined');
  const product = mapProduct(res);
  const salesPageUrl =
    product.type === 'otp-bundle'
      ? toOtpBundleCheckout(res)
      : product.type === 'otp'
      ? toProgramCheckoutUrl({
          influencer: {
            customURL,
          },
          id: parseInt(product.value.otpId, 10),
          name: product.value.name,
        })
      : toSubscriptionPurchasePage({ customURL });

  return {
    detailURL: '',
    product,
    name: coupon.name,
    code: coupon.code,
    discountId: parseInt(coupon.id, 10),
    amount: mapAmount(coupon),
    createdAt: new Date(coupon.createdAt).getTime(),
    expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).getTime() : null,
    redemptionsLimit: coupon.maxRedemptions || null,
    redemptions: coupon.redemptions,
    status: mapStatus(coupon),
    providerCouponId: '',
    url: `${salesPageUrl}?promo=${coupon.code}`,
    isProductPublished: !toDisabledCheckoutLink(res),
  };
};

const toOtpBundleCheckout = (res: ServerCouponDetails) => {
  const bundle = res.paidOtpBundles && res.paidOtpBundles[0];
  return bundle?.checkoutPage?.url || '';
};

export const toDisabledCheckoutLink = (res: ServerCouponDetails) => {
  const bundle = res.paidOtpBundles && res.paidOtpBundles[0];
  if (!bundle) return false;

  return bundle.status !== BundleStatus.Published;
};
